import type { LocalHistoryCiphertext } from '../historySecrets/types';
import type { DirectMessageReplyReference } from '@shared/types';
import { canUseNativeChatStorage, nativeChatStorage } from '../utils/webviewBridge';

const DB_NAME = 'family-messenger-messages';
const DB_VERSION = 5;
const STORE_NAME = 'messages';
const THREADS_STORE_NAME = 'threads';

const MAX_TIMESTAMP = Number.MAX_SAFE_INTEGER;
const NATIVE_DIRECT_MIGRATION_KEY = 'circlus.nativeChatStorage.directMessagesMigrated.v1';
let directNativeMigrationStarted = false;

export function buildThreadKey(a: string, b: string): string {
  const left = (a || '').trim();
  const right = (b || '').trim();
  if (!left || !right) return `${left}::${right}`;
  return left < right ? `${left}::${right}` : `${right}::${left}`;
}

export type MessageDirection = 'inbound' | 'outbound';

export type DirectFileEventPayload = {
  sessionId: string;
  fileName: string;
  size: number;
  mimeType: string;
  transferDirection: 'sent' | 'received';
  localAvailable?: boolean;
};

export type StoredMessage = {
  serverMessageId: string;
  clientMessageId?: string;
  senderIdentityId: string;
  recipientIdentityId: string;
  threadKey?: string;
  // Optional sender public key included by server for decrypting messages
  // even if the sender isn't saved in the local vault yet.
  senderIdentityPublicKey?: { algorithm: 'ed25519' | 'x25519'; value: string };
  senderIdentityName?: string;
  ciphertext: string;
  senderCiphertext?: string;
  senderSignature: string;
  epoch?: number | null;
  serverTimestamp: number;
  editedAt?: number | null;
  deletedAt?: number | null;
  contentUpdatedAt?: number | null;
  revision?: number;
  // Gapless, monotonic per-direct-chat sequence number assigned by the server.
  // Used as the focused per-chat sync cursor (since = max local chatSeq).
  chatSeq?: number;
  replyTo?: DirectMessageReplyReference | null;
  status: 'sending' | 'new' | 'delivered' | 'read' | 'failed';
  direction: MessageDirection;
  kind?: 'encrypted' | 'direct_file_event';
  directFileEvent?: DirectFileEventPayload;
  localHistoryCiphertext?: LocalHistoryCiphertext | null;
};

export type ThreadSummary = {
  threadKey: string;
  participantA: string;
  participantB: string;
  lastTimestamp: number;
  lastMessageId: string;
  lastMessage: StoredMessage;
  unreadByIdentity: Record<string, number>;
};

function isNativeDirectStoragePrimary(): boolean {
  if (!canUseNativeChatStorage()) return false;
  try {
    return localStorage.getItem(NATIVE_DIRECT_MIGRATION_KEY) === '1';
  } catch {
    return false;
  }
}

function markNativeDirectStoragePrimary(): void {
  try {
    localStorage.setItem(NATIVE_DIRECT_MIGRATION_KEY, '1');
  } catch {
    // If localStorage is unavailable, keep IndexedDB as the primary readable cache.
  }
}

function scheduleDirectNativeMigration(): void {
  if (!canUseNativeChatStorage() || isNativeDirectStoragePrimary() || directNativeMigrationStarted) return;
  directNativeMigrationStarted = true;
  window.setTimeout(() => {
    void migrateDirectMessagesToNative()
      .catch(() => undefined)
      .finally(() => {
        directNativeMigrationStarted = false;
      });
  }, 250);
}

async function migrateDirectMessagesToNative(): Promise<void> {
  if (!canUseNativeChatStorage() || isNativeDirectStoragePrimary()) return;
  const rows = await readAllDirectMessagesFromIndexedDb();
  if (rows.length === 0) {
    markNativeDirectStoragePrimary();
    window.dispatchEvent(new Event('messages:updated'));
    return;
  }
  if (!nativeChatStorage.clearDirectMessages()) {
    return;
  }
  if (!nativeChatStorage.saveDirectMessages(JSON.stringify(rows.map(sanitizeStoredMessage)))) {
    return;
  }
  await clearDirectMessagesIndexedDb();
  markNativeDirectStoragePrimary();
  window.dispatchEvent(new Event('messages:updated'));
}

function parseNativeDirectPage<T>(raw: string | null, key: string): { rows: T[]; hasMore: boolean } | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { [name: string]: unknown; hasMore?: unknown };
    const rows = Array.isArray(parsed?.[key]) ? parsed[key] as T[] : [];
    return { rows, hasMore: parsed.hasMore === true };
  } catch {
    return null;
  }
}

function parseNativeArray<T>(raw: string | null): T[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as T[] : null;
  } catch {
    return null;
  }
}

function parseNativeObject<T>(raw: string | null): T | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const oldVersion = event.oldVersion;

      let store: IDBObjectStore;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        store = db.createObjectStore(STORE_NAME, { keyPath: 'serverMessageId' });
        store.createIndex('byTimestamp', 'serverTimestamp');
        store.createIndex('byThread', 'threadKey');
        store.createIndex('byThreadTimestamp', ['threadKey', 'serverTimestamp']);
        store.createIndex('byStatusTimestamp', ['status', 'serverTimestamp']);
        store.createIndex('bySender', 'senderIdentityId');
        store.createIndex('byRecipient', 'recipientIdentityId');
      } else {
        store = request.transaction!.objectStore(STORE_NAME);
        if (!store.indexNames.contains('byTimestamp')) {
          store.createIndex('byTimestamp', 'serverTimestamp');
        }
        if (!store.indexNames.contains('byThread')) {
          store.createIndex('byThread', 'threadKey');
        }
        if (!store.indexNames.contains('byThreadTimestamp')) {
          store.createIndex('byThreadTimestamp', ['threadKey', 'serverTimestamp']);
        }
        if (!store.indexNames.contains('byStatusTimestamp')) {
          store.createIndex('byStatusTimestamp', ['status', 'serverTimestamp']);
        }
        if (!store.indexNames.contains('bySender')) {
          store.createIndex('bySender', 'senderIdentityId');
        }
        if (!store.indexNames.contains('byRecipient')) {
          store.createIndex('byRecipient', 'recipientIdentityId');
        }
      }

      // Thread summaries store.
      let threadsStore: IDBObjectStore;
      if (!db.objectStoreNames.contains(THREADS_STORE_NAME)) {
        threadsStore = db.createObjectStore(THREADS_STORE_NAME, { keyPath: 'threadKey' });
        threadsStore.createIndex('byLastTimestamp', 'lastTimestamp');
      } else {
        threadsStore = request.transaction!.objectStore(THREADS_STORE_NAME);
        if (!threadsStore.indexNames.contains('byLastTimestamp')) {
          threadsStore.createIndex('byLastTimestamp', 'lastTimestamp');
        }
      }

      // v2 migration: ensure serverTimestamp is a number (older records may have string BIGINT).
      if (oldVersion < 2) {
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) return;
          const value: any = cursor.value;
          if (value && typeof value.serverTimestamp !== 'number') {
            const n = Number(value.serverTimestamp);
            if (Number.isFinite(n)) {
              value.serverTimestamp = n;
              cursor.update(value);
            }
          }
          cursor.continue();
        };
      }

      // v3 migration: ensure threadKey exists and can be indexed.
      if (oldVersion < 3) {
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) return;

          const value: any = cursor.value;
          if (value) {
            if (typeof value.serverTimestamp !== 'number') {
              const n = Number(value.serverTimestamp);
              if (Number.isFinite(n)) value.serverTimestamp = n;
            }

            if (typeof value.threadKey !== 'string' || !value.threadKey) {
              value.threadKey = buildThreadKey(value.senderIdentityId, value.recipientIdentityId);
            }

            cursor.update(value);
          }

          cursor.continue();
        };
      }

      // v4 migration: (re)build per-thread summaries.
      if (oldVersion < 4) {
        // Clear any stale data (in case of partially created DBs).
        try {
          threadsStore.clear();
        } catch {
          // ignore
        }

        const summaries = new Map<string, ThreadSummary>();
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) {
            for (const summary of summaries.values()) {
              threadsStore.put(summary);
            }
            return;
          }

          const message = cursor.value as StoredMessage;
          const threadKey =
            typeof message.threadKey === 'string' && message.threadKey
              ? message.threadKey
              : buildThreadKey(message.senderIdentityId, message.recipientIdentityId);

          let summary = summaries.get(threadKey);
          if (!summary) {
            summary = {
              threadKey,
              participantA: (message.senderIdentityId || '').trim(),
              participantB: (message.recipientIdentityId || '').trim(),
              lastTimestamp: message.serverTimestamp,
              lastMessageId: message.serverMessageId,
              lastMessage: sanitizeStoredMessage(message),
              unreadByIdentity: {} as Record<string, number>
            };
            summaries.set(threadKey, summary);
          }

          if (message.serverTimestamp >= summary.lastTimestamp) {
            summary.lastTimestamp = message.serverTimestamp;
            summary.lastMessageId = message.serverMessageId;
            summary.lastMessage = sanitizeStoredMessage(message);
          }

          if (message.direction === 'inbound' && message.status !== 'read') {
            const recipient = (message.recipientIdentityId || '').trim();
            if (recipient) {
              summary.unreadByIdentity[recipient] = (summary.unreadByIdentity[recipient] || 0) + 1;
            }
          }

          cursor.continue();
        };
      }

      // v5 migration: when a local-history ciphertext exists, stop retaining
      // chat-key-decryptable ciphertext in IndexedDB.
      if (oldVersion < 5) {
        const sanitize = (value: any) => {
          if (
            value?.localHistoryCiphertext
            && !value.deletedAt
            && value.kind !== 'direct_file_event'
            && value.status !== 'sending'
            && value.status !== 'failed'
          ) {
            value.ciphertext = '';
            delete value.senderCiphertext;
          }
          return value;
        };

        const messageCursorReq = store.openCursor();
        messageCursorReq.onsuccess = () => {
          const cursor = messageCursorReq.result;
          if (!cursor) return;
          cursor.update(sanitize(cursor.value));
          cursor.continue();
        };

        const threadCursorReq = threadsStore.openCursor();
        threadCursorReq.onsuccess = () => {
          const cursor = threadCursorReq.result;
          if (!cursor) return;
          const summary = cursor.value as ThreadSummary;
          if (summary?.lastMessage) {
            summary.lastMessage = sanitize(summary.lastMessage) as StoredMessage;
            cursor.update(summary);
          }
          cursor.continue();
        };
      }
    };
  });
}

async function readAllDirectMessagesFromIndexedDb(): Promise<StoredMessage[]> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    return await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve((req.result || []) as StoredMessage[]);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

function getDirectMessageCreatedAt(message: StoredMessage): number {
  return Number(message.serverTimestamp || 0);
}

function isDirectMessageInCreatedRange(
  message: StoredMessage,
  startInclusive: number,
  endExclusive: number
): boolean {
  const createdAt = getDirectMessageCreatedAt(message);
  return Number.isFinite(createdAt) && createdAt >= startInclusive && createdAt < endExclusive;
}

export async function getDirectMessagesCreatedInRange(
  startInclusive: number,
  endExclusive: number
): Promise<StoredMessage[]> {
  if (isNativeDirectStoragePrimary()) {
    const summariesPage = parseNativeDirectPage<ThreadSummary>(
      nativeChatStorage.getDirectThreadSummariesPage(JSON.stringify({ limit: 10000 })),
      'summaries'
    );
    if (summariesPage) {
      const rows: StoredMessage[] = [];
      for (const summary of summariesPage.rows) {
        const page = parseNativeDirectPage<StoredMessage>(
          nativeChatStorage.getDirectThreadMessagesPage(summary.threadKey, JSON.stringify({ limit: 10000 })),
          'messages'
        );
        if (!page) continue;
        rows.push(...page.rows.filter((message) => isDirectMessageInCreatedRange(message, startInclusive, endExclusive)));
      }
      rows.sort((a, b) => getDirectMessageCreatedAt(a) - getDirectMessageCreatedAt(b));
      return rows.map(sanitizeStoredMessage);
    }
  } else {
    scheduleDirectNativeMigration();
  }

  const rows = await readAllDirectMessagesFromIndexedDb();
  return rows
    .filter((message) => isDirectMessageInCreatedRange(message, startInclusive, endExclusive))
    .sort((a, b) => getDirectMessageCreatedAt(a) - getDirectMessageCreatedAt(b))
    .map(sanitizeStoredMessage);
}

async function clearDirectMessagesIndexedDb(): Promise<void> {
  const db = await openDB();
  try {
    const tx = db.transaction([STORE_NAME, THREADS_STORE_NAME], 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.objectStore(THREADS_STORE_NAME).clear();
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T>
): Promise<T> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    fn(store)
      .then(resolve)
      .catch(reject);
  });
}

async function withMessagesAndThreadsStores<T>(
  mode: IDBTransactionMode,
  fn: (stores: { messages: IDBObjectStore; threads: IDBObjectStore }, tx: IDBTransaction) => void
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction([STORE_NAME, THREADS_STORE_NAME], mode);
    const messages = tx.objectStore(STORE_NAME);
    const threads = tx.objectStore(THREADS_STORE_NAME);

    // IMPORTANT:
    // Do not let callers overwrite tx.oncomplete, otherwise this Promise will never resolve.
    // Centralize completion handling here.
    tx.oncomplete = () => {
      window.dispatchEvent(new Event('messages:updated'));
      resolve(undefined as unknown as T);
      db.close();
    };
    tx.onerror = () => {
      reject(tx.error);
      db.close();
    };
    tx.onabort = () => {
      reject(tx.error);
      db.close();
    };

    fn({ messages, threads }, tx);
  });
}

async function withThreadsStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T>
): Promise<T> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(THREADS_STORE_NAME, mode);
    const store = tx.objectStore(THREADS_STORE_NAME);
    fn(store)
      .then(resolve)
      .catch(reject);
  });
}

function isUnreadInbound(message: StoredMessage | undefined): boolean {
  return Boolean(message && message.direction === 'inbound' && message.status !== 'read');
}

function isMessageContentChanged(existing: StoredMessage | undefined, incoming: StoredMessage): boolean {
  if (!existing) return false;
  if (incoming.deletedAt) return true;
  if (typeof incoming.revision === 'number' && typeof existing.revision === 'number' && incoming.revision !== existing.revision) return true;
  if (typeof incoming.contentUpdatedAt === 'number' && typeof existing.contentUpdatedAt === 'number' && incoming.contentUpdatedAt !== existing.contentUpdatedAt) return true;
  if (typeof incoming.editedAt === 'number' && incoming.editedAt !== existing.editedAt) return true;
  return false;
}

function sanitizeStoredMessage(message: StoredMessage): StoredMessage {
  if (
    message.localHistoryCiphertext
    && !message.deletedAt
    && message.kind !== 'direct_file_event'
    && message.status !== 'sending'
    && message.status !== 'failed'
  ) {
    return {
      ...message,
      ciphertext: '',
      senderCiphertext: undefined
    };
  }
  return message;
}

export async function saveMessages(messages: StoredMessage[]): Promise<void> {
  const list = Array.isArray(messages) ? messages : [];
  if (canUseNativeChatStorage()) {
    nativeChatStorage.saveDirectMessages(JSON.stringify(list.map(sanitizeStoredMessage)));
    if (isNativeDirectStoragePrimary()) {
      window.dispatchEvent(new Event('messages:updated'));
      return;
    }
    scheduleDirectNativeMigration();
  }
  await withMessagesAndThreadsStores<void>('readwrite', ({ messages: messagesStore, threads: threadsStore }, tx) => {
    let idx = 0;

    const processNext = () => {
      if (idx >= list.length) return;
      const message = list[idx++];
      const threadKey =
        (message as any).threadKey || buildThreadKey(message.senderIdentityId, message.recipientIdentityId);

      const getExistingReq = messagesStore.get(message.serverMessageId);
      getExistingReq.onerror = () => tx.abort();
      getExistingReq.onsuccess = () => {
        const existing = getExistingReq.result as StoredMessage | undefined;
        const contentChanged = isMessageContentChanged(existing, message);

        const withThreadKey: StoredMessage = {
          // Merge: start from existing so no field is lost when callers pass partial updates.
          // Incoming message fields overwrite existing ones (newer data wins),
          // except status which must never downgrade from 'read'.
          ...existing,
          ...message,
          threadKey,
          status: existing?.status === 'read' ? 'read' : message.status,
          localHistoryCiphertext:
            message.localHistoryCiphertext === undefined
              ? (contentChanged ? undefined : existing?.localHistoryCiphertext)
              : message.localHistoryCiphertext
        };
        const storedMessage = sanitizeStoredMessage(withThreadKey);

        messagesStore.put(storedMessage);

        const getSummaryReq = threadsStore.get(threadKey);
        getSummaryReq.onerror = () => tx.abort();
        getSummaryReq.onsuccess = () => {
          const summary = (getSummaryReq.result as ThreadSummary | undefined) || {
            threadKey,
            participantA: (storedMessage.senderIdentityId || '').trim(),
            participantB: (storedMessage.recipientIdentityId || '').trim(),
            lastTimestamp: storedMessage.serverTimestamp,
            lastMessageId: storedMessage.serverMessageId,
            lastMessage: storedMessage,
            unreadByIdentity: {} as Record<string, number>
          };

          // Keep last message snapshot up to date.
          if (storedMessage.serverTimestamp >= summary.lastTimestamp) {
            summary.lastTimestamp = storedMessage.serverTimestamp;
            summary.lastMessageId = storedMessage.serverMessageId;
            summary.lastMessage = storedMessage;
          } else if (summary.lastMessageId === storedMessage.serverMessageId) {
            summary.lastMessage = storedMessage;
          }

          // Adjust unread counters (handle upserts / status changes).
          const wasUnread = isUnreadInbound(existing);
          const isUnread = isUnreadInbound(storedMessage);
          if (wasUnread !== isUnread) {
            const recipient = (storedMessage.recipientIdentityId || '').trim();
            if (recipient) {
              const prev = summary.unreadByIdentity[recipient] || 0;
              const next = Math.max(0, prev + (isUnread ? 1 : -1));
              if (next === 0) {
                delete summary.unreadByIdentity[recipient];
              } else {
                summary.unreadByIdentity[recipient] = next;
              }
            }
          }

          threadsStore.put(summary);
          processNext();
        };
      };
    };

    processNext();
  });
}

export async function saveLocalDirectFileEvent(params: {
  sessionId: string;
  localIdentityId: string;
  peerIdentityId: string;
  fileName: string;
  size: number;
  mimeType: string;
  direction: 'outgoing' | 'incoming';
  localAvailable?: boolean;
}): Promise<StoredMessage> {
  const now = Date.now();
  const outbound = params.direction === 'outgoing';
  const serverMessageId = `local-direct-file:${params.sessionId}:${outbound ? 'sent' : 'received'}`;
  const message: StoredMessage = {
    serverMessageId,
    senderIdentityId: outbound ? params.localIdentityId : params.peerIdentityId,
    recipientIdentityId: outbound ? params.peerIdentityId : params.localIdentityId,
    ciphertext: '',
    senderSignature: '',
    serverTimestamp: now,
    status: outbound ? 'delivered' : 'read',
    direction: outbound ? 'outbound' : 'inbound',
    kind: 'direct_file_event',
    directFileEvent: {
      sessionId: params.sessionId,
      fileName: params.fileName,
      size: params.size,
      mimeType: params.mimeType,
      transferDirection: outbound ? 'sent' : 'received',
      localAvailable: params.localAvailable
    }
  };

  await saveMessages([message]);
  return message;
}

export async function updateDirectFileEventAvailability(
  serverMessageId: string,
  localAvailable: boolean
): Promise<void> {
  if (isNativeDirectStoragePrimary()) {
    const existing = parseNativeObject<StoredMessage>(nativeChatStorage.getDirectMessageById(serverMessageId));
    if (!existing?.directFileEvent) return;
    await saveMessages([{
      ...existing,
      directFileEvent: {
        ...existing.directFileEvent,
        localAvailable
      }
    }]);
    return;
  } else {
    scheduleDirectNativeMigration();
  }
  await withMessagesAndThreadsStores<void>('readwrite', ({ messages: messagesStore, threads: threadsStore }, tx) => {
    const request = messagesStore.get(serverMessageId);
    request.onerror = () => tx.abort();
    request.onsuccess = () => {
      const existing = request.result as StoredMessage | undefined;
      if (!existing?.directFileEvent) return;

      const updated: StoredMessage = {
        ...existing,
        directFileEvent: {
          ...existing.directFileEvent,
          localAvailable
        }
      };
      const threadKey =
        (existing as any).threadKey || buildThreadKey(existing.senderIdentityId, existing.recipientIdentityId);
      updated.threadKey = threadKey;

      messagesStore.put(updated);

      const getSummaryReq = threadsStore.get(threadKey);
      getSummaryReq.onerror = () => tx.abort();
      getSummaryReq.onsuccess = () => {
        const summary = getSummaryReq.result as ThreadSummary | undefined;
        if (!summary) return;
        if (summary.lastMessageId === updated.serverMessageId) {
          summary.lastMessage = updated;
          threadsStore.put(summary);
        }
      };
    };
  });
}

export async function updateMessageStatus(
  serverMessageId: string,
  status: StoredMessage['status']
): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.updateDirectMessageStatus(serverMessageId, status);
    if (isNativeDirectStoragePrimary()) {
      window.dispatchEvent(new Event('messages:updated'));
      return;
    }
    scheduleDirectNativeMigration();
  }
  await withMessagesAndThreadsStores<void>('readwrite', ({ messages: messagesStore, threads: threadsStore }, tx) => {
    const request = messagesStore.get(serverMessageId);
    request.onerror = () => tx.abort();
    request.onsuccess = () => {
      const existing = request.result as StoredMessage | undefined;
      if (!existing) return;

      const updated: StoredMessage = { ...existing, status };
      const threadKey =
        (existing as any).threadKey || buildThreadKey(existing.senderIdentityId, existing.recipientIdentityId);
      updated.threadKey = threadKey;

      messagesStore.put(updated);

      const getSummaryReq = threadsStore.get(threadKey);
      getSummaryReq.onerror = () => tx.abort();
      getSummaryReq.onsuccess = () => {
        const summary = getSummaryReq.result as ThreadSummary | undefined;
        if (!summary) return;

        // Adjust unread if inbound status flips to/from read.
        const wasUnread = isUnreadInbound(existing);
        const isUnread = isUnreadInbound(updated);
        if (wasUnread !== isUnread) {
          const recipient = (updated.recipientIdentityId || '').trim();
          if (recipient) {
            const prev = summary.unreadByIdentity[recipient] || 0;
            const next = Math.max(0, prev + (isUnread ? 1 : -1));
            if (next === 0) {
              delete summary.unreadByIdentity[recipient];
            } else {
              summary.unreadByIdentity[recipient] = next;
            }
          }
        }

        // Keep last message snapshot current if this is the last message.
        if (summary.lastMessageId === updated.serverMessageId) {
          summary.lastMessage = updated;
          if (typeof updated.serverTimestamp === 'number' && Number.isFinite(updated.serverTimestamp)) {
            summary.lastTimestamp = updated.serverTimestamp;
          }
        }

        threadsStore.put(summary);
      };
    };

  });
}

export async function deleteMessage(serverMessageId: string): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.deleteDirectMessage(serverMessageId);
    if (isNativeDirectStoragePrimary()) {
      window.dispatchEvent(new Event('messages:updated'));
      return;
    }
    scheduleDirectNativeMigration();
  }
  await withMessagesAndThreadsStores<void>('readwrite', ({ messages: messagesStore, threads: threadsStore }, tx) => {
    const getReq = messagesStore.get(serverMessageId);
    getReq.onerror = () => tx.abort();
    getReq.onsuccess = () => {
      const existing = getReq.result as StoredMessage | undefined;
      if (!existing) return;

      const threadKey =
        (existing as any).threadKey || buildThreadKey(existing.senderIdentityId, existing.recipientIdentityId);

      messagesStore.delete(serverMessageId);

      const summaryReq = threadsStore.get(threadKey);
      summaryReq.onerror = () => tx.abort();
      summaryReq.onsuccess = () => {
        const summary = summaryReq.result as ThreadSummary | undefined;
        if (!summary) return;

        // Adjust unread counter if needed.
        if (isUnreadInbound(existing)) {
          const recipient = (existing.recipientIdentityId || '').trim();
          if (recipient) {
            const prev = summary.unreadByIdentity[recipient] || 0;
            const next = Math.max(0, prev - 1);
            if (next === 0) {
              delete summary.unreadByIdentity[recipient];
            } else {
              summary.unreadByIdentity[recipient] = next;
            }
          }
        }

        if (summary.lastMessageId !== serverMessageId) {
          threadsStore.put(summary);
          return;
        }

        // Deleted the last message; find the new last message in this thread.
        const index = messagesStore.index('byThreadTimestamp');
        const range = IDBKeyRange.bound([threadKey, 0], [threadKey, MAX_TIMESTAMP]);
        const cursorReq = index.openCursor(range, 'prev');
        cursorReq.onerror = () => tx.abort();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) {
            threadsStore.delete(threadKey);
            return;
          }
          const nextLast = cursor.value as StoredMessage;
          summary.lastMessageId = nextLast.serverMessageId;
          summary.lastTimestamp = nextLast.serverTimestamp;
          summary.lastMessage = nextLast;
          threadsStore.put(summary);
        };
      };
    };

  });
}

export async function deleteThreadMessages(
  currentIdentityId: string,
  peerIdentityId: string
): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.deleteDirectThreadMessages(currentIdentityId, peerIdentityId);
    if (isNativeDirectStoragePrimary()) {
      window.dispatchEvent(new Event('messages:updated'));
      return;
    }
    scheduleDirectNativeMigration();
  }
  await withMessagesAndThreadsStores<void>('readwrite', ({ messages: messagesStore, threads: threadsStore }, tx) => {
    const threadKey = buildThreadKey(currentIdentityId, peerIdentityId);
    const index = messagesStore.index('byThread');
    const cursorReq = index.openCursor(IDBKeyRange.only(threadKey));

    cursorReq.onerror = () => tx.abort();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) {
        // This threadKey includes only these two identities, so it is safe to drop the summary.
        threadsStore.delete(threadKey);
        return;
      }

      const message = cursor.value as StoredMessage;
      // Only allow deleting within the scope of the current local identity.
      const belongsToCurrentIdentity =
        message.senderIdentityId === currentIdentityId
        || message.recipientIdentityId === currentIdentityId;
      if (!belongsToCurrentIdentity) {
        cursor.continue();
        return;
      }
      const isOutgoing = message.senderIdentityId === currentIdentityId;
      const peerId = isOutgoing ? message.recipientIdentityId : message.senderIdentityId;
      if (peerId === peerIdentityId) {
        cursor.delete();
      }

      cursor.continue();
    };

  });
}

export async function deleteIdentityMessages(identityId: string): Promise<void> {
  const target = (identityId || '').trim();
  if (!target) return;
  const page = await getThreadSummariesPage({ limit: 100000 });
  const peers = new Set<string>();
  for (const summary of page.summaries) {
    if (summary.participantA === target && summary.participantB) peers.add(summary.participantB);
    if (summary.participantB === target && summary.participantA) peers.add(summary.participantA);
  }
  for (const peerId of peers) {
    await deleteThreadMessages(target, peerId);
  }
}

export async function markThreadReadUpTo(
  localIdentityId: string,
  peerIdentityId: string,
  readThrough: number
): Promise<void> {
  if (canUseNativeChatStorage()) {
    nativeChatStorage.markDirectThreadReadUpTo(localIdentityId, peerIdentityId, readThrough);
    if (isNativeDirectStoragePrimary()) {
      window.dispatchEvent(new Event('messages:updated'));
      return;
    }
    scheduleDirectNativeMigration();
  }
  const threadKey = buildThreadKey(localIdentityId, peerIdentityId);

  await withMessagesAndThreadsStores<void>('readwrite', ({ messages: messagesStore, threads: threadsStore }, tx) => {
    const index = messagesStore.index('byThreadTimestamp');
    const range = IDBKeyRange.bound([threadKey, 0], [threadKey, readThrough]);
    const cursorReq = index.openCursor(range);

    let updatedCount = 0;

    cursorReq.onerror = () => tx.abort();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        const message = cursor.value as StoredMessage;
        if (isUnreadInbound(message)) {
          cursor.update({ ...message, status: 'read' });
          updatedCount++;
        }
        cursor.continue();
        return;
      }

      // Cursor exhausted — update thread summary unread counter.
      if (updatedCount === 0) return;

      const summaryReq = threadsStore.get(threadKey);
      summaryReq.onerror = () => tx.abort();
      summaryReq.onsuccess = () => {
        const summary = summaryReq.result as ThreadSummary | undefined;
        if (!summary) return;

        const prev = summary.unreadByIdentity[localIdentityId] || 0;
        const next = Math.max(0, prev - updatedCount);
        if (next === 0) {
          delete summary.unreadByIdentity[localIdentityId];
        } else {
          summary.unreadByIdentity[localIdentityId] = next;
        }

        // Keep lastMessage snapshot in sync if it was one of the messages we just marked read.
        if (summary.lastMessage && isUnreadInbound(summary.lastMessage) &&
            summary.lastMessage.serverTimestamp <= readThrough) {
          summary.lastMessage = { ...summary.lastMessage, status: 'read' };
        }

        threadsStore.put(summary);
      };
    };
  });
}

export async function getMessageById(serverMessageId: string): Promise<StoredMessage | undefined> {
  if (isNativeDirectStoragePrimary()) {
    const message = parseNativeObject<StoredMessage>(nativeChatStorage.getDirectMessageById(serverMessageId));
    if (message) return message;
  } else {
    scheduleDirectNativeMigration();
  }
  return withStore('readonly', (store) => {
    return new Promise((resolve, reject) => {
      const request = store.get(serverMessageId);
      request.onsuccess = () => resolve(request.result as StoredMessage | undefined);
      request.onerror = () => reject(request.error);
    });
  });
}

export async function getFailedMessages(): Promise<StoredMessage[]> {
  if (isNativeDirectStoragePrimary()) {
    const rows = parseNativeArray<StoredMessage>(nativeChatStorage.getFailedDirectMessages());
    if (rows) return rows;
  } else {
    scheduleDirectNativeMigration();
  }
  return withStore('readonly', (store) => {
    return new Promise((resolve, reject) => {
      const index = store.index('byStatusTimestamp');
      const range = IDBKeyRange.bound(['failed', 0], ['failed', MAX_TIMESTAMP]);
      const result: StoredMessage[] = [];
      const req = index.openCursor(range, 'prev');
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          resolve(result);
          return;
        }
        const message = cursor.value as StoredMessage;
        if (message.direction === 'outbound') {
          result.push(message);
        }
        cursor.continue();
      };
    });
  });
}

type ThreadSummariesPageOptions = {
  limit: number;
  beforeTimestamp?: number;
};

export async function getThreadSummariesPage(
  options: ThreadSummariesPageOptions
): Promise<{ summaries: ThreadSummary[]; hasMore: boolean }> {
  if (isNativeDirectStoragePrimary()) {
    const page = parseNativeDirectPage<ThreadSummary>(
      nativeChatStorage.getDirectThreadSummariesPage(JSON.stringify(options)),
      'summaries'
    );
    if (page) return { summaries: page.rows, hasMore: page.hasMore };
  } else {
    scheduleDirectNativeMigration();
  }
  const limit = Math.max(1, Math.floor(options.limit));
  const maxToRead = limit + 1;

  return withThreadsStore('readonly', (store) => {
    return new Promise((resolve, reject) => {
      const index = store.index('byLastTimestamp');
      const upper =
        typeof options.beforeTimestamp === 'number' && Number.isFinite(options.beforeTimestamp)
          ? options.beforeTimestamp
          : MAX_TIMESTAMP;

      // If beforeTimestamp is provided, exclude it (so paging doesn't repeat the last item).
      const excludeUpper = typeof options.beforeTimestamp === 'number' && Number.isFinite(options.beforeTimestamp);
      const range = IDBKeyRange.bound(0, upper, false, excludeUpper);

      const itemsDesc: ThreadSummary[] = [];
      const req = index.openCursor(range, 'prev');
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          const hasMore = itemsDesc.length > limit;
          const slice = hasMore ? itemsDesc.slice(0, limit) : itemsDesc;
          resolve({ summaries: slice, hasMore });
          return;
        }

        itemsDesc.push(cursor.value as ThreadSummary);

        if (itemsDesc.length >= maxToRead) {
          resolve({ summaries: itemsDesc.slice(0, limit), hasMore: true });
          return;
        }

        cursor.continue();
      };
    });
  });
}

export async function countUnreadThreadsForIdentities(identityIds: string[]): Promise<number> {
  const localSet = new Set(identityIds.map((id) => (id || '').trim()).filter(Boolean));
  if (localSet.size === 0) return 0;
  if (isNativeDirectStoragePrimary()) {
    const page = parseNativeDirectPage<ThreadSummary>(
      nativeChatStorage.getDirectThreadSummariesPage(JSON.stringify({ limit: 10000 })),
      'summaries'
    );
    if (page) {
      let count = 0;
      for (const summary of page.rows) {
        const a = (summary.participantA || '').trim();
        const b = (summary.participantB || '').trim();
        const unread = summary.unreadByIdentity || {};
        const unreadA = a && localSet.has(a) ? (unread[a] || 0) : 0;
        const unreadB = b && localSet.has(b) ? (unread[b] || 0) : 0;
        if (unreadA + unreadB > 0) count += 1;
      }
      return count;
    }
  } else {
    scheduleDirectNativeMigration();
  }

  return withThreadsStore('readonly', (store) => {
    return new Promise((resolve, reject) => {
      const index = store.index('byLastTimestamp');
      let count = 0;
      const req = index.openCursor(null, 'prev');
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          resolve(count);
          return;
        }

        const summary = cursor.value as ThreadSummary;
        const a = (summary.participantA || '').trim();
        const b = (summary.participantB || '').trim();
        if (a || b) {
          const unread = summary.unreadByIdentity || ({} as Record<string, number>);
          const unreadA = a && localSet.has(a) ? (unread[a] || 0) : 0;
          const unreadB = b && localSet.has(b) ? (unread[b] || 0) : 0;
          if (unreadA + unreadB > 0) count += 1;
        }

        cursor.continue();
      };
    });
  });
}

/**
 * Returns the highest chat_seq stored locally for a thread, or 0 when none is
 * present. Used as the focused per-chat sync cursor: `since = max local chatSeq`.
 * Underestimating is safe (server re-sends already-stored rows, dedup is by
 * serverMessageId), so a missing chatSeq simply contributes 0.
 */
export async function getThreadMaxChatSeq(threadKey: string): Promise<number> {
  const reduceMax = (rows: StoredMessage[]): number =>
    rows.reduce((max, message) => {
      const seq = Number(message.chatSeq);
      return Number.isFinite(seq) && seq > max ? seq : max;
    }, 0);

  if (isNativeDirectStoragePrimary()) {
    const page = parseNativeDirectPage<StoredMessage>(
      nativeChatStorage.getDirectThreadMessagesPage(threadKey, JSON.stringify({ limit: 100000 })),
      'messages'
    );
    if (page) return reduceMax(page.rows);
  } else {
    scheduleDirectNativeMigration();
  }

  return withStore('readonly', (store) => {
    return new Promise((resolve, reject) => {
      const index = store.index('byThread');
      const req = index.openCursor(IDBKeyRange.only(threadKey));
      let max = 0;
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          resolve(max);
          return;
        }
        const seq = Number((cursor.value as StoredMessage).chatSeq);
        if (Number.isFinite(seq) && seq > max) max = seq;
        cursor.continue();
      };
    });
  });
}

type ThreadMessagesPageOptions = {
  limit: number;
  beforeTimestamp?: number;
};

export async function getThreadMessagesPage(
  threadKey: string,
  options: ThreadMessagesPageOptions
): Promise<{ messages: StoredMessage[]; hasMore: boolean }> {
  if (isNativeDirectStoragePrimary()) {
    const page = parseNativeDirectPage<StoredMessage>(
      nativeChatStorage.getDirectThreadMessagesPage(threadKey, JSON.stringify(options)),
      'messages'
    );
    if (page) return { messages: page.rows, hasMore: page.hasMore };
  } else {
    scheduleDirectNativeMigration();
  }
  const limit = Math.max(1, Math.floor(options.limit));
  const maxToRead = limit + 1;

  return withStore('readonly', (store) => {
    return new Promise((resolve, reject) => {
      const index = store.index('byThreadTimestamp');

      const upper =
        typeof options.beforeTimestamp === 'number' && Number.isFinite(options.beforeTimestamp)
          ? options.beforeTimestamp
          : MAX_TIMESTAMP;

      const range = IDBKeyRange.bound([threadKey, 0], [threadKey, upper], false, typeof options.beforeTimestamp === 'number');

      const itemsDesc: StoredMessage[] = [];
      const req = index.openCursor(range, 'prev');

      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const cursor = req.result;

        if (!cursor) {
          const hasMore = itemsDesc.length > limit;
          const slice = hasMore ? itemsDesc.slice(0, limit) : itemsDesc;
          slice.reverse();
          resolve({ messages: slice, hasMore });
          return;
        }

        itemsDesc.push(cursor.value as StoredMessage);

        if (itemsDesc.length >= maxToRead) {
          const slice = itemsDesc.slice(0, limit);
          slice.reverse();
          resolve({ messages: slice, hasMore: true });
          return;
        }

        cursor.continue();
      };
    });
  });
}
